// /home/labdoo/zitech-nexus/frontend/src/pages/admin/AdminSupportTicketsList.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supportAPI } from '../../services/api';
import './AdminSupportManagement.css';

// SVG Icons
const Icons = {
  View: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  User: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Ticket: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  StatusDot: () => (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
      <circle cx="4" cy="4" r="4" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ast-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Empty: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Calendar: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Priority: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7L12 12L22 7L12 2Z" />
      <path d="M2 17L12 22L22 17" />
      <path d="M2 12L12 17L22 12" />
    </svg>
  ),
};

interface SupportTicket {
  id: number;
  ticket_number: string;
  name: string;
  email: string;
  subject_display: string;
  message: string;
  status: string;
  status_display: string;
  priority: string;
  priority_display: string;
  created_at: string;
  updated_at: string;
}

const AdminSupportTicketsList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await supportAPI.adminGetAllTickets();
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: number, status: string) => {
    try {
      await supportAPI.adminUpdateTicketStatus(id, status);
      fetchTickets();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update ticket status');
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'open': return '#f59e0b';
      case 'in_progress': return '#0ea5e9';
      case 'resolved': return '#10b981';
      case 'closed': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusBackground = (status: string) => {
    switch(status) {
      case 'open': return '#fef3c7';
      case 'in_progress': return '#dbeafe';
      case 'resolved': return '#d1fae5';
      case 'closed': return '#f3f4f6';
      default: return '#f3f4f6';
    }
  };

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'open': return 'Open';
      case 'in_progress': return 'In Progress';
      case 'resolved': return 'Resolved';
      case 'closed': return 'Closed';
      default: return status;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch(priority) {
      case 'urgent': return '#ef4444';
      case 'high': return '#f59e0b';
      case 'medium': return '#0ea5e9';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getPriorityBackground = (priority: string) => {
    switch(priority) {
      case 'urgent': return '#fee2e2';
      case 'high': return '#fef3c7';
      case 'medium': return '#dbeafe';
      case 'low': return '#d1fae5';
      default: return '#f3f4f6';
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (statusFilter !== 'all' && ticket.status !== statusFilter) return false;
    return true;
  });

  const counts = {
    all: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    closed: tickets.filter(t => t.status === 'closed').length,
  };

  if (loading) {
    return (
      <div className="ast-loading-container">
        <div className="ast-spinner">
          <Icons.Spinner />
        </div>
        <p className="ast-loading-text">Loading tickets...</p>
      </div>
    );
  }

  return (
    <div className="ast-container">
      {/* Header */}
      <div className="ast-header">
        <div className="ast-header-left">
          <h1 className="ast-title">Support Tickets</h1>
          <p className="ast-subtitle">Manage and respond to customer support tickets</p>
        </div>
        <div className="ast-header-actions">
          <span className="ast-stats-badge">
            <Icons.Ticket />
            Total: {counts.all}
          </span>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="ast-stats-bar">
        <div className="ast-stat-item">
          <span className="ast-stat-label">Total</span>
          <span className="ast-stat-value">{counts.all}</span>
        </div>
        <div className="ast-stat-divider"></div>
        <div className="ast-stat-item">
          <span className="ast-stat-label">Open</span>
          <span className="ast-stat-value ast-stat-open">{counts.open}</span>
        </div>
        <div className="ast-stat-divider"></div>
        <div className="ast-stat-item">
          <span className="ast-stat-label">In Progress</span>
          <span className="ast-stat-value ast-stat-progress">{counts.in_progress}</span>
        </div>
        <div className="ast-stat-divider"></div>
        <div className="ast-stat-item">
          <span className="ast-stat-label">Resolved</span>
          <span className="ast-stat-value ast-stat-resolved">{counts.resolved}</span>
        </div>
        <div className="ast-stat-divider"></div>
        <div className="ast-stat-item">
          <span className="ast-stat-label">Closed</span>
          <span className="ast-stat-value ast-stat-closed">{counts.closed}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="ast-filter-bar">
        <button 
          className={`ast-filter-btn ${statusFilter === 'all' ? 'ast-filter-active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          All
          <span className="ast-filter-count">{counts.all}</span>
        </button>
        <button 
          className={`ast-filter-btn ${statusFilter === 'open' ? 'ast-filter-active' : ''}`}
          onClick={() => setStatusFilter('open')}
        >
          <span className="ast-status-dot-filter ast-dot-open"></span>
          Open
          <span className="ast-filter-count">{counts.open}</span>
        </button>
        <button 
          className={`ast-filter-btn ${statusFilter === 'in_progress' ? 'ast-filter-active' : ''}`}
          onClick={() => setStatusFilter('in_progress')}
        >
          <span className="ast-status-dot-filter ast-dot-progress"></span>
          In Progress
          <span className="ast-filter-count">{counts.in_progress}</span>
        </button>
        <button 
          className={`ast-filter-btn ${statusFilter === 'resolved' ? 'ast-filter-active' : ''}`}
          onClick={() => setStatusFilter('resolved')}
        >
          <span className="ast-status-dot-filter ast-dot-resolved"></span>
          Resolved
          <span className="ast-filter-count">{counts.resolved}</span>
        </button>
        <button 
          className={`ast-filter-btn ${statusFilter === 'closed' ? 'ast-filter-active' : ''}`}
          onClick={() => setStatusFilter('closed')}
        >
          <span className="ast-status-dot-filter ast-dot-closed"></span>
          Closed
          <span className="ast-filter-count">{counts.closed}</span>
        </button>
      </div>

      {/* Table */}
      <div className="ast-table-wrapper">
        <table className="ast-table">
          <thead>
            <tr>
              <th className="ast-col-ticket">Ticket</th>
              <th className="ast-col-customer">Customer</th>
              <th className="ast-col-subject">Subject</th>
              <th className="ast-col-priority">Priority</th>
              <th className="ast-col-status">Status</th>
              <th className="ast-col-date">Created</th>
              <th className="ast-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTickets.length === 0 ? (
              <tr>
                <td colSpan={7} className="ast-empty-state">
                  <Icons.Empty />
                  <p className="ast-empty-text">No tickets found</p>
                  <p className="ast-empty-subtext">Support tickets will appear here once customers reach out</p>
                </td>
              </tr>
            ) : (
              filteredTickets.map((ticket) => (
                <tr key={ticket.id} className="ast-table-row">
                  <td className="ast-col-ticket">
                    <span className="ast-ticket-number">{ticket.ticket_number}</span>
                  </td>
                  <td className="ast-col-customer">
                    <div className="ast-customer-info">
                      <span className="ast-customer-name">
                        <Icons.User />
                        {ticket.name}
                      </span>
                      <span className="ast-customer-email">{ticket.email}</span>
                    </div>
                  </td>
                  <td className="ast-col-subject">
                    <span className="ast-subject">{ticket.subject_display}</span>
                  </td>
                  <td className="ast-col-priority">
                    <span 
                      className="ast-priority-badge"
                      style={{
                        background: getPriorityBackground(ticket.priority),
                        color: getPriorityColor(ticket.priority)
                      }}
                    >
                      <span className="ast-priority-dot" style={{ background: getPriorityColor(ticket.priority) }}></span>
                      {ticket.priority_display}
                    </span>
                  </td>
                  <td className="ast-col-status">
                    <span 
                      className="ast-status-badge"
                      style={{
                        background: getStatusBackground(ticket.status),
                        color: getStatusColor(ticket.status)
                      }}
                    >
                      <Icons.StatusDot />
                      {getStatusLabel(ticket.status)}
                    </span>
                  </td>
                  <td className="ast-col-date">
                    <span className="ast-date-text">
                      <Icons.Calendar />
                      {new Date(ticket.created_at).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                  </td>
                  <td className="ast-col-actions">
                    <div className="ast-actions">
                      <button 
                        className="ast-action-btn ast-action-view"
                        onClick={() => navigate(`/dashboard/admin/support/tickets/${ticket.id}`)}
                        title="View Ticket"
                      >
                        <Icons.View />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminSupportTicketsList;